import { useQuery } from '@tanstack/react-query';

import api from '@/hooks/useApi';

// ============================================================================
// Types
// ============================================================================

export interface Toggle {
  toggleId: string;
  enabled: boolean;
  config?: Record<string, unknown>;
}

export interface TogglesResponse {
  toggles: Toggle[];
}

// ============================================================================
// Query Keys
// ============================================================================

export const toggleKeys = {
  all: ['toggles'] as const,
  list: () => [...toggleKeys.all, 'list'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch all feature toggles for the current environment.
 * Returns toggles with their enabled state and optional config.
 */
export function useToggles() {
  return useQuery({
    queryKey: toggleKeys.list(),
    queryFn: async () => {
      const response = await api.get<TogglesResponse>('/toggles');
      return response.data.toggles;
    },
    // Toggles change infrequently, cache for 5 minutes
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Check if a specific toggle is enabled.
 * Returns false if the toggle doesn't exist or fetch fails.
 *
 * @param toggleId - The toggle to check
 * @returns true if enabled, false otherwise
 */
export function useToggle(toggleId: string): boolean {
  const { data: toggles } = useToggles();
  const toggle = toggles?.find((t) => t.toggleId === toggleId);
  return toggle?.enabled ?? false;
}

/**
 * Get the config for a specific toggle.
 * Returns undefined if the toggle doesn't exist or has no config.
 *
 * @param toggleId - The toggle to get config for
 * @returns The toggle config or undefined
 */
export function useToggleConfig<T extends Record<string, unknown>>(
  toggleId: string
): T | undefined {
  const { data: toggles } = useToggles();
  const toggle = toggles?.find((t) => t.toggleId === toggleId);
  return toggle?.config as T | undefined;
}

// ============================================================================
// Toggle IDs (type-safe constants)
// ============================================================================

export const TOGGLE_IDS = {
  SUBSCRIPTIONS: 'subscriptions',
  READING_PREFERENCES: 'reading-preferences',
  NOTIFICATION_PREFERENCES: 'notification-preferences',
} as const;
